/**
 * POS Apps — organization Settings → Integrations.
 * Read-only display of globally-managed PosHub applications.
 * Enable/disable is controlled by administrators via Django admin.
 */
import { Alert, Card, Flex, Spin, Typography } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getOrganizationPosHubApplications } from 'api/poshub';
import { AdminContext } from 'constants/contexts';
import { captureException } from 'utils/errors';
import { sectionCardProps } from './settingsShared';

const { Text } = Typography;

const LOCAL_LOGO_BY_NAME = [
  { test: (n) => /toast/i.test(n), logo: require('assets/image/integrations/toast.svg') },
  { test: (n) => /shopify/i.test(n), logo: require('assets/image/integrations/shopify.png') },
  { test: (n) => /square/i.test(n), logo: require('assets/image/integrations/square.png') },
  { test: (n) => /clover/i.test(n), logo: require('assets/image/integrations/clover.png') },
];

function localLogoForName(name) {
  const hit = LOCAL_LOGO_BY_NAME.find((x) => x.test(name || ''));
  return hit ? hit.logo : null;
}

function IntegrationLogo({ name, imageUrl }) {
  const [imgError, setImgError] = useState(false);
  const local = localLogoForName(name);
  const src = !imgError && imageUrl ? imageUrl : local;
  const hasImg = Boolean(src);

  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 8,
        backgroundColor: hasImg ? '#fff' : 'rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        padding: 4,
        boxSizing: 'border-box',
      }}
    >
      {hasImg ? (
        <img
          src={src?.default ?? src}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }}
          onError={() => setImgError(true)}
        />
      ) : (
        <span style={{ fontSize: 16, fontWeight: 600, color: 'rgba(0, 0, 0, 0.45)' }}>
          {(name || '?').charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  );
}

function POSAppRow({ app, isLast }) {
  return (
    <Flex
      align="flex-start"
      gap={12}
      style={{
        padding: '12px 0',
        borderBottom: isLast ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
      }}
    >
      <IntegrationLogo name={app.name} imageUrl={app.image_url} />
      <div style={{ minWidth: 0 }}>
        <Text strong style={{ display: 'block', fontSize: 14, lineHeight: 1.4, color: 'rgba(0, 0, 0, 0.88)' }}>
          {app.name || 'Application'}
        </Text>
        {app.description ? (
          <Text style={{
            display: 'block',
            fontSize: 12,
            lineHeight: 1.4,
            color: 'rgba(0, 0, 0, 0.65)',
            marginTop: 2,
          }}
          >
            {app.description}
          </Text>
        ) : null}
      </div>
    </Flex>
  );
}

function POSIntegrations() {
  const { organizationId } = useContext(AdminContext);
  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!organizationId) return Promise.resolve();
    setLoading(true);
    setError(null);
    return getOrganizationPosHubApplications(organizationId)
      .then((list) => {
        setApps(Array.isArray(list) ? list.filter((app) => app.is_enabled) : []);
      })
      .catch((e) => {
        captureException(e);
        setError(e?.response?.data?.detail || e?.message || 'Failed to load POS applications');
        setApps([]);
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!organizationId) {
    return (
      <Alert type="info" showIcon message="Select an organization in the header to manage POS apps." />
    );
  }

  return (
    <Card {...sectionCardProps} title="POS Apps">
      <Text style={{
        color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
      }}
      >
        These POS apps will be available for restaurants to connect with.
      </Text>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {loading ? (
        <Spin />
      ) : apps.length === 0 && !error ? (
        <Text style={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: 12 }}>No applications found.</Text>
      ) : (
        <div>
          {apps.map((app, index) => (
            <POSAppRow
              key={app.poshub_application_id}
              app={app}
              isLast={index === apps.length - 1}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

export default POSIntegrations;
